/*
 * Fetches approaching / applied free-shipping promo data from the discounts-info custom API.
 * Returns { discountMsg, fillPercent, isApplied, isLoading } for the progress bar.
 * Hidden for GlobalE international customers (countryISO !== 'US').
 */
import {useState, useEffect} from 'react'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useGlobaleContext} from '../contexts/globale'
import CustomApi from './use-custom-api'
import {DISCOUNTS_INFO_API} from '../constants'

/**
 * @returns {{
 *   discountMsg: string,
 *   fillPercent: number,
 *   isApplied: boolean,
 *   isLoading: boolean,
 *   isHidden: boolean
 * }}
 */
export default function useApproachingFreeShipping() {
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const {data: basket} = useCurrentBasket()
    const {countryISO, currencyCode} = useGlobaleContext()

    const [discountMsg, setDiscountMsg] = useState('')
    const [remainingPercent, setRemainingPercent] = useState(0)
    const [isApplied, setIsApplied] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    // GlobalE international customers don't see free shipping bar
    const geRestricted = countryISO && countryISO !== 'US'

    useEffect(() => {
        if (geRestricted || !basket?.basketId) {
            setIsLoading(false)
            return
        }

        let cancelled = false
        const fetchDiscounts = async () => {
            setIsLoading(true)
            try {
                const customapi = new CustomApi()
                const token = await getTokenWhenReady()
                const parameters = {
                    c_currencyCode: currencyCode,
                    c_countryISO: countryISO,
                    c_subTotal: basket?.productSubTotal || basket?.orderTotal
                }
                const response = await customapi.get(
                    token,
                    DISCOUNTS_INFO_API,
                    '/',
                    parameters,
                    proxy
                )

                if (cancelled) return

                let msg = ''
                let remaining = 0
                let applied = false

                // Approaching discounts (filter out loyalty promos)
                const approachingDiscounts = (response?.approachingDiscounts || []).filter(
                    (d) => !d.isLoyalty
                )

                if (approachingDiscounts.length > 0) {
                    msg = approachingDiscounts[0].discountMsg
                    remaining = approachingDiscounts[0].remainingPercent
                }

                // Applied shipping promo takes priority — bar should be 100% full
                if (response?.appliedShippingPromo?.length) {
                    msg = response.appliedShippingPromo[0].discountMsg
                    remaining = 100
                    applied = true
                }

                setDiscountMsg(msg)
                setRemainingPercent(remaining)
                setIsApplied(applied)
            } catch (error) {
                console.error('Failed to fetch approaching free shipping data:', error)
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        fetchDiscounts()
        return () => {
            cancelled = true
        }
    }, [basket?.productSubTotal, basket?.basketId, countryISO, currencyCode])

    // The API returns remainingPercent as the fill value (e.g. 82 = 82% filled)
    const fillPercent = remainingPercent || 0
    const isHidden = geRestricted || (!discountMsg && !isLoading)

    return {discountMsg, fillPercent, isApplied, isLoading, isHidden}
}
